import React from 'react';
import styled from 'styled-components';
import {
  UserPlus, ArrowRight, StickyNote, MessageCircle,
  Sparkles, XCircle, MessageSquare
} from 'lucide-react';
import type { LeadActivity, ActivityType } from '../../../../Data/leads.dto';

// ─── Activity config ─────────────────────────────────────────
const ACTIVITY_CONFIG: Record<ActivityType, {
  icon: React.FC<any>;
  color: string;
  bgColor: string;
  label: string;
}> = {
  lead_created:  { icon: UserPlus,       color: '#16a34a', bgColor: '#f0fdf4', label: 'Lead Created' },
  stage_change:  { icon: ArrowRight,     color: '#2563eb', bgColor: '#eff6ff', label: 'Stage Changed' },
  note_added:    { icon: StickyNote,     color: '#6b7280', bgColor: '#f9fafb', label: 'Note Added' },
  follow_up_sent:{ icon: MessageCircle,  color: '#7c3aed', bgColor: '#f5f3ff', label: 'Follow-up Sent' },
  ai_scored:     { icon: Sparkles,       color: '#d97706', bgColor: '#fffbeb', label: 'AI Scored' },
  marked_lost:   { icon: XCircle,        color: '#dc2626', bgColor: '#fef2f2', label: 'Marked Lost' },
  whatsapp_sent: { icon: MessageSquare,  color: '#0d9488', bgColor: '#f0fdfa', label: 'WhatsApp Sent' },
};

// ─── Styled components ───────────────────────────────────────
const Item = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;
  &:last-child { border-bottom: none; }
`;

const IconWrap = styled.div<{ color: string; bgColor: string }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${p => p.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const TypeLabel = styled.span<{ color: string }>`
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.color};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Description = styled.p`
  font-size: 14px;
  color: #374151;
  margin: 2px 0 0;
  line-height: 1.5;
`;

const MetaLine = styled.p`
  font-size: 12px;
  color: #9ca3af;
  margin: 4px 0 0;
`;

const TimeStamp = styled.span`
  font-size: 12px;
  color: #9ca3af;
  white-space: nowrap;
  flex-shrink: 0;
`;

// ─── Helpers ─────────────────────────────────────────────────
const formatRelativeTime = (dateStr: string): string => {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1)  return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24)  return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

import { STAGE_LABELS } from '../../../../Data/leads.dto';
import type { LeadStage } from '../../../../Data/leads.dto';

const getStageLabel = (val: any): string => {
  if (!val) return '';
  const cleaned = String(val).trim().toUpperCase();
  return STAGE_LABELS[cleaned as LeadStage] ?? val;
};

const renderDescription = (activity: LeadActivity): React.ReactNode => {
  let { activity_type, meta, note } = activity;
  if (typeof meta === 'string') {
    try {
      meta = JSON.parse(meta);
    } catch (e) {}
  }
  switch (activity_type as any) {
    case 'lead_created':
      return `Lead created${meta?.source ? ` from ${meta.source}` : ''}`;
    case 'stage_change': {
      const fromVal = meta?.from_stage ?? meta?.old_stage ?? meta?.from ?? meta?.fromStage ?? meta?.oldStage ?? meta?.old ?? meta?.previous_stage ?? meta?.previous ?? (activity as any).from_stage;
      const toVal = meta?.to_stage ?? meta?.new_stage ?? meta?.to ?? meta?.toStage ?? meta?.newStage ?? meta?.new ?? meta?.next_stage ?? meta?.next ?? (activity as any).to_stage;
      
      const fromLabel = getStageLabel(fromVal);
      const toLabel = getStageLabel(toVal);
      
      if (!fromLabel && !toLabel) {
        if (note) return note;
        return '—';
      }

      return (
        <span>
          <strong>{fromLabel || '—'}</strong>
          {' → '}
          <strong>{toLabel || '—'}</strong>
        </span>
      );
    }
    case 'note_added':
      return note ?? '—';
    case 'follow_up_sent':
      return (
        <>
          {`Follow-up Touch #${meta?.touch_number ?? '?'} sent`}
          {meta?.wallet_charge_inr && (
            <MetaLine>
              ₹{meta.wallet_charge_inr} charged · Balance after: ₹{meta.wallet_balance_after ?? '—'}
            </MetaLine>
          )}
        </>
      );
    case 'ai_scored':
      return `AI scored: ${meta?.score ?? '—'} — Priority ${meta?.ai_priority ?? '—'}`;
    case 'marked_lost':
      return `Marked lost${meta?.reason ? `: ${meta.reason}` : ''}`;
    case 'whatsapp_sent':
      return 'WhatsApp message sent manually';
    default:
      return note ?? activity_type;
  }
};

// ─── Component ───────────────────────────────────────────────
interface Props {
  activity: LeadActivity;
}

const LeadActivityItem: React.FC<Props> = ({ activity }) => {
  const config = ACTIVITY_CONFIG[activity.activity_type] ?? ACTIVITY_CONFIG.note_added;
  const Icon = config.icon;

  return (
    <Item>
      <IconWrap color={config.color} bgColor={config.bgColor}>
        <Icon size={16} color={config.color} />
      </IconWrap>
      <Body>
        <TypeLabel color={config.color}>{config.label}</TypeLabel>
        <Description>{renderDescription(activity)}</Description>
        {activity.created_by_name && (
          <MetaLine>by {activity.created_by_name}</MetaLine>
        )}
      </Body>
      <TimeStamp title={new Date(activity.created_at).toLocaleString('en-IN')}>
        {formatRelativeTime(activity.created_at)}
      </TimeStamp>
    </Item>
  );
};

export default LeadActivityItem;
